import { createReducer, on } from '@ngrx/store';
import { AlarmsManagerModel } from '../../models/alarms-manager.model';
import * as AlarmsManagerActions from './alarms-manager.action';

export interface AlarmsManagerState {
  alarms: AlarmsManagerModel.Alarm[];
  selectedAlarm: AlarmsManagerModel.Alarm | null;
  selectedAlarmWithAlerts: AlarmsManagerModel.AlarmWithAlerts | null;
  stats: AlarmsManagerModel.AlarmStatsResponse | null;
  currentFilter: AlarmsManagerModel.GetAlarmsQuery;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  isLoading: boolean;
  error: string | null;
}

export const initialState: AlarmsManagerState = {
  alarms: [],
  selectedAlarm: null,
  selectedAlarmWithAlerts: null,
  stats: null,
  currentFilter: { page: 1, limit: 20 },
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  isLoading: false,
  error: null,
};

export const alarmsManagerReducer = createReducer(
  initialState,
  
  // Reset state
  on(AlarmsManagerActions.resetToInitialState, () => initialState),

  // Get Alarms
  on(AlarmsManagerActions.getAlarms, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmsSuccess, (state, { response }) => ({
    ...state,
    alarms: response.alarms,
    totalCount: response.total,
    currentPage: response.page,
    totalPages: Math.ceil(response.total / response.limit),
    isLoading: false,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Get Alarm By ID
  on(AlarmsManagerActions.getAlarmById, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmByIdSuccess, (state, { alarm }) => ({
    ...state,
    selectedAlarm: alarm,
    isLoading: false,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmByIdFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Get Alarm With Alerts
  on(AlarmsManagerActions.getAlarmWithAlerts, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmWithAlertsSuccess, (state, { alarmWithAlerts }) => ({
    ...state,
    selectedAlarmWithAlerts: alarmWithAlerts,
    isLoading: false,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmWithAlertsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Close Alarm
  on(AlarmsManagerActions.closeAlarm, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AlarmsManagerActions.closeAlarmSuccess, (state, { alarmId }) => ({
    ...state,
    alarms: state.alarms.map(alarm => 
      alarm.id === alarmId 
        ? { ...alarm, status: AlarmsManagerModel.AlarmStatus.CLOSED, updatedAt: Date.now() }
        : alarm
    ),
    isLoading: false,
    error: null,
  })),
  on(AlarmsManagerActions.closeAlarmFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Get Alarm Stats
  on(AlarmsManagerActions.getAlarmStats, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmStatsSuccess, (state, { stats }) => ({
    ...state,
    stats,
    isLoading: false,
    error: null,
  })),
  on(AlarmsManagerActions.getAlarmStatsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // Check Timeouts
  on(AlarmsManagerActions.checkTimeouts, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(AlarmsManagerActions.checkTimeoutsSuccess, (state, { updatedCount }) => ({
    ...state,
    isLoading: false,
    error: null,
  })),
  on(AlarmsManagerActions.checkTimeoutsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  // UI State
  on(AlarmsManagerActions.setAlarmsLoading, (state, { isLoading }) => ({
    ...state,
    isLoading,
  })),
  on(AlarmsManagerActions.setSelectedAlarm, (state, { alarm }) => ({
    ...state,
    selectedAlarm: alarm,
  })),
  on(AlarmsManagerActions.clearSelectedAlarmWithAlerts, (state) => ({
    ...state,
    selectedAlarmWithAlerts: null,
  })),
  on(AlarmsManagerActions.setAlarmFilter, (state, { filter }) => ({
    ...state,
    currentFilter: filter,
  })),
  on(AlarmsManagerActions.setCurrentPage, (state, { page }) => ({
    ...state,
    currentPage: page,
    currentFilter: { ...state.currentFilter, page },
  })),
);